import React, { useState, useEffect } from "react";
import payrollService from "../../services/payrollService";
import PayrollCard from "../../components/Payroll/PayrollCard";
import PayrollFilters from "../../components/Payroll/PayrollFilters";
import Notification from "../../components/UI/Notification";
import { Download, Eye, AlertCircle } from "lucide-react";
import ReactPaginate from "react-paginate";
import { useNavigate } from "react-router-dom";

const MyPayrolls = () => {
  const [payrolls, setPayrolls] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [filters, setFilters] = useState({ year: "", month: "", per_page: 12 });
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 });
  const navigate = useNavigate();

  useEffect(() => {
    fetchPayrolls();
    fetchSummary();
  }, [filters, pagination.current_page]);

  const fetchPayrolls = async () => {
    setLoading(true);
    try {
      const response = await payrollService.getMyPayrolls({
        ...filters,
        page: pagination.current_page,
      });
      if (response.success) {
        setPayrolls(response.data.data || []);
        setPagination({
          current_page: response.data.current_page,
          last_page: response.data.last_page,
          total: response.data.total,
        });
      }
    } catch (error) {
      setNotification({ type: "error", message: "Failed to fetch payrolls" });
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await payrollService.getMyPayrollSummary();
      if (response.success) {
        setSummary(response.data);
      }
    } catch (error) {
      console.error("Error fetching summary:", error);
    }
  };

  const handleDownloadPayslip = async (id) => {
    try {
      const response = await payrollService.getPayslipDownloadLink(id);
      if (response.success) {
        window.open(response.data.download_url, "_blank");
      }
    } catch (error) {
      setNotification({ type: "error", message: "Failed to download payslip" });
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300",
      processed: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
      approved: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
      paid: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300",
    };
    return colors[status] || colors.draft;
  };

  return (
    <div className="p-6 font-poppins">
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          My Payrolls
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          View and download your payslips
        </p>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <PayrollCard
            title="Total Earnings"
            value={`₹${summary.total_earnings?.toLocaleString("en-IN")}`}
            subtitle="Year to date"
          />
          <PayrollCard
            title="Average Salary"
            value={`₹${summary.average_salary?.toLocaleString("en-IN")}`}
            subtitle="Monthly average"
          />
          <PayrollCard
            title="Total Payrolls"
            value={summary.total_payrolls}
            subtitle="Records"
          />
        </div>
      )}

      {/* Filters */}
      <PayrollFilters
        filters={filters}
        onFilterChange={(key, value) => setFilters((prev) => ({ ...prev, [key]: value }))}
        showYearMonth={true}
        showSearch={false}
      />

      {/* Payrolls Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
        {loading ? (
          <div className="col-span-full flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : payrolls.length > 0 ? (
          payrolls.map((payroll) => (
            <div
              key={payroll.id}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {payroll.payroll_year}-{String(payroll.payroll_month).padStart(2, "0")}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {new Date(payroll.payroll_year, payroll.payroll_month - 1).toLocaleString("default", {
                      month: "long",
                      year: "numeric",
                    })}
                  </p>
                </div>
                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(payroll.status)}`}>
                  {payroll.status}
                </span>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Gross Salary</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    ₹{parseFloat(payroll.gross_salary).toLocaleString("en-IN")}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Total Deductions</span>
                  <span className="font-medium text-red-600 dark:text-red-400">
                    -₹{parseFloat(payroll.total_deductions).toLocaleString("en-IN")}
                  </span>
                </div>
                <div className="flex justify-between text-base font-semibold pt-2 border-t border-gray-200 dark:border-gray-700">
                  <span className="text-gray-900 dark:text-white">Net Salary</span>
                  <span className="text-green-600 dark:text-green-400">
                    ₹{parseFloat(payroll.net_salary).toLocaleString("en-IN")}
                  </span>
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => navigate(`/payroll-details/${payroll.id}`)}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium transition-colors"
                >
                  <Eye size={16} />
                  View Details
                </button>
                {payroll.status === "paid" && (
                  <button
                    onClick={() => handleDownloadPayslip(payroll.id)}
                    className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium transition-colors"
                    title="Download Payslip"
                  >
                    <Download size={16} />
                  </button>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full bg-white dark:bg-gray-800 rounded-lg shadow-md p-12 text-center">
            <AlertCircle className="mx-auto text-gray-400 mb-4" size={48} />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              No Payrolls Found
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              No payroll records available for the selected filters
            </p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.last_page > 1 && (
        <div className="flex justify-center">
          <ReactPaginate
            previousLabel="Previous"
            nextLabel="Next"
            breakLabel="..."
            pageCount={pagination.last_page}
            marginPagesDisplayed={2}
            pageRangeDisplayed={5}
            onPageChange={({ selected }) =>
              setPagination((prev) => ({ ...prev, current_page: selected + 1 }))
            }
            containerClassName="flex items-center gap-2"
            pageClassName="px-3 py-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
            pageLinkClassName="text-gray-700 dark:text-gray-300"
            previousClassName="px-3 py-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
            previousLinkClassName="text-gray-700 dark:text-gray-300"
            nextClassName="px-3 py-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
            nextLinkClassName="text-gray-700 dark:text-gray-300"
            activeClassName="bg-blue-600 text-white hover:bg-blue-700"
            disabledClassName="opacity-50 cursor-not-allowed"
          />
        </div>
      )}
    </div>
  );
};

export default MyPayrolls;
